import { PAYMENT_TYPE_TRANSFER } from '@klear/payment/constants'
import { createToastConfig, useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'
import { trpc, type RouterOutputs } from '@klear/ui/lib/trpc'

import type { TransferCreateWire } from '@/app/transfers/transfer-create-form'

type TransferCreateResult = RouterOutputs['transfer']['create']
type TransferList = RouterOutputs['payment']['list']

export function useTransferList(): {
  transfers: TransferList
  error: ReturnType<typeof trpc.payment.list.useQuery>['error']
  isLoading: boolean
} {
  const { data, error, isLoading } = trpc.payment.list.useQuery(
    { paymentType: [PAYMENT_TYPE_TRANSFER] },
    { refetchOnWindowFocus: true }
  )
  return { transfers: data ?? [], error, isLoading }
}

export function useAllowedTransferOrgs() {
  const { data, error, isLoading } = trpc.transfer.listAllowedOrgs.useQuery()
  return { allowedOrgs: data ?? [], error, isLoading }
}

export function useTransfer(paymentId: number | undefined) {
  const query = trpc.payment.get.useQuery(
    { id: paymentId ?? 0 },
    {
      enabled: paymentId != null && paymentId > 0,
      refetchOnWindowFocus: true
    }
  )
  const isNonTransferPayment = Boolean(query.data && query.data.paymentType !== PAYMENT_TYPE_TRANSFER)
  const transfer = query.data && query.data.paymentType === PAYMENT_TYPE_TRANSFER ? query.data : null
  return { ...query, transfer, isNonTransferPayment }
}

const invalidateQueries = ['payment.list', 'payment.get']

export function useTransferMutations() {
  const createMutation = useMutationWithToast(
    trpc.transfer.create.useMutation,
    createToastConfig<TransferCreateResult>('Transfer', 'create', (data) => `#${data.paymentId}`),
    { invalidateQueries }
  )

  const createTransfer = (input: TransferCreateWire) => createMutation.mutateAsync(input)

  return {
    createTransfer,
    isCreating: createMutation.isPending
  }
}
